import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Autocomplete,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Refresh, Add, Edit, Visibility } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fetchEmployees, createEmployee, updateEmployee, fetchDepartments, fetchPositions } from '../../services/employee';
import { getUsers as getUsersList } from '../../services/user';
import CustomPagination from '../../components/common/CustomPagination.js';

const Employees = () => {
  const { t } = useTranslation(['common', 'employees']);
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    location: '',
    department: '',
    manager: '',
    hr_manager: ''
  });
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 20,
    totalCount: 0
  });
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingEmployee, setViewingEmployee] = useState(null);
  const [form, setForm] = useState({
    user_id: '',
    full_name: '',
    first_name: '',
    last_name: '',
    middle_name: '',
    date_of_birth: '',
    gender: '',
    nationality: '',
    national_id: '',
    passport_number: '',
    phone: '',
    email: '',
    alternate_email: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postal_code: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: '',
    status: 'active',
    employment_type: 'full_time',
    location: 'nigeria',
    department: '',
    manager: '',
    hr_manager: '',
    hire_date: '',
    probation_end_date: '',
    position_id: '',
    notes: ''
  });
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [managers, setManagers] = useState([]);
  const [hrManagers, setHrManagers] = useState([]);
  const [positions, setPositions] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [saving, setSaving] = useState(false);

  const totalPages = Math.ceil(pagination.totalCount / pagination.rowsPerPage) || 1;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        ...filters,
        page: pagination.page + 1,
        page_size: pagination.rowsPerPage
      };
      
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await fetchEmployees(params);
      
      if (response.results) {
        setEmployees(response.results);
        setPagination(prev => ({
          ...prev,
          totalCount: response.count || 0
        }));
      } else {
        setEmployees(Array.isArray(response) ? response : []);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch employees');
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch departments, managers, HR managers, positions for dropdowns
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [deptsRes, employeesRes, positionsRes, hrRes] = await Promise.all([
          fetchDepartments({ page_size: 1000 }).catch(() => ({ results: [] })),
          fetchEmployees({ page_size: 1000 }).catch(() => ({ results: [] })),
          fetchPositions({ page_size: 1000 }).catch(() => ({ results: [] })),
          getUsersList({ role: 'admin', page_size: 100 }).catch(() => ({ results: [] }))
        ]);
        
        setDepartments(deptsRes.results || deptsRes || []);
        setManagers(employeesRes.results || employeesRes || []);
        setPositions(positionsRes.results || positionsRes || []);
        setHrManagers(hrRes.results || hrRes || []);
      } catch (err) {
        console.error('Failed to fetch dropdown data:', err);
      }
    };
    
    if (modalOpen) {
      fetchDropdownData();
    }
  }, [modalOpen]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setPagination(prev => ({ ...prev, rowsPerPage: newRowsPerPage, page: 0 }));
  };

  const handleCustomRowsInputChange = (value) => {
    setCustomRowsInput(value);
  };

  const handleCustomRowsInputBlur = () => {
    const value = parseInt(customRowsInput);
    if (value && value > 0 && value <= 1000) {
      setPagination(prev => ({ ...prev, rowsPerPage: value, page: 0 }));
      setCustomRowsActive(false);
      setCustomRowsInput('');
    } else {
      setCustomRowsInput('');
      setCustomRowsActive(false);
    }
  };

  const handleViewEmployee = (employee) => {
    setViewingEmployee(employee);
    setViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setViewingEmployee(null);
  };

  const handleOpenModal = async (employee = null) => {
    if (employee) {
      setEditId(employee.id);

      // Split full_name into first, middle, last names for editing
      let firstName = employee.first_name || '';
      let middleName = employee.middle_name || '';
      let lastName = employee.last_name || '';
      let fullName = employee.full_name || '';

      // If we have full_name but not separate names, try to split it
      if (fullName && (!firstName || !lastName)) {
        const nameParts = fullName.trim().split(' ');
        if (nameParts.length >= 2) {
          firstName = nameParts[0];
          lastName = nameParts[nameParts.length - 1];
          if (nameParts.length > 2) {
            middleName = nameParts.slice(1, -1).join(' ');
          }
        }
      }

      setForm({
        user_id: employee.user?.sid || employee.user?.id || employee.user || '',
        full_name: fullName,
        employee_id: employee.employee_id || '',
        employee_number: employee.employee_number || '',
        first_name: firstName,
        last_name: lastName,
        middle_name: middleName,
        date_of_birth: employee.date_of_birth || '',
        gender: employee.gender || '',
        nationality: employee.nationality || '',
        national_id: employee.national_id || '',
        passport_number: employee.passport_number || '',
        phone: employee.phone || '',
        email: employee.email || '',
        alternate_email: employee.alternate_email || '',
        address: employee.address || '',
        city: employee.city || '',
        state: employee.state || '',
        country: employee.country || '',
        postal_code: employee.postal_code || '',
        emergency_contact_name: employee.emergency_contact_name || '',
        emergency_contact_phone: employee.emergency_contact_phone || '',
        emergency_contact_relationship: employee.emergency_contact_relationship || '',
        status: employee.status || 'active',
        employment_type: employee.employment_type || 'full_time',
        location: employee.location || 'nigeria',
        department: employee.department?.id || employee.department || '',
        manager: employee.manager?.id || employee.manager || '',
        hr_manager: employee.hr_manager?.id || employee.hr_manager || '',
        hire_date: employee.hire_date || '',
        probation_end_date: employee.probation_end_date || '',
        position_id: employee.current_position?.id || '',
        notes: employee.notes || ''
      });
    } else {
      setEditId(null);
      setUsers([]); // Clear users for new employee creation
      setSelectedUser(null); // Clear selected user for new employee
      setUsersLoading(false);
      if (searchTimeout) {
        clearTimeout(searchTimeout);
        setSearchTimeout(null);
      }
      setForm({
        user_id: '',
        full_name: '',
        first_name: '',
        last_name: '',
        middle_name: '',
        date_of_birth: '',
        gender: '',
        nationality: '',
        national_id: '',
        passport_number: '',
        phone: '',
        email: '',
        alternate_email: '',
        address: '',
        city: '',
        state: '',
        country: '',
        postal_code: '',
        emergency_contact_name: '',
        emergency_contact_phone: '',
        emergency_contact_relationship: '',
        status: 'active',
        employment_type: 'full_time',
        location: 'nigeria',
        department: '',
        manager: '',
        hr_manager: '',
        hire_date: '',
        probation_end_date: '',
        position_id: '',
        notes: ''
      });
    }
    
    // Users will be loaded dynamically when searching
    
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditId(null);
    setUsers([]); // Clear users when closing modal
    setSelectedUser(null); // Clear selected user
    setUsersLoading(false);
    if (searchTimeout) {
      clearTimeout(searchTimeout);
      setSearchTimeout(null);
    }
    setForm({
      user_id: '',
      full_name: '',
      first_name: '',
      last_name: '',
      middle_name: '',
      date_of_birth: '',
      gender: '',
      nationality: '',
      national_id: '',
      passport_number: '',
      phone: '',
      email: '',
      alternate_email: '',
      address: '',
      city: '',
      state: '',
      country: '',
      postal_code: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      emergency_contact_relationship: '',
      status: 'active',
      employment_type: 'full_time',
      location: 'nigeria',
      department: '',
      manager: '',
      hr_manager: '',
      hire_date: '',
      probation_end_date: '',
      position_id: '',
      notes: ''
    });
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    // Validation
    if (!form.user_id && !editId) {
      setSnackbar({ open: true, message: 'Please select a user', severity: 'error' });
      return;
    }

    if (!form.full_name.trim()) {
      setSnackbar({ open: true, message: 'Full name is required', severity: 'error' });
      return;
    }

    if (!form.hire_date) {
      setSnackbar({ open: true, message: 'Hire date is required', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      // Build full name from first, middle, and last names
      const nameParts = [form.first_name, form.middle_name, form.last_name].filter(Boolean);
      const fullName = nameParts.join(' ');

      const payload = {
        ...(editId ? {} : { user: form.user_id }),
        // employee_id and employee_number are auto-generated, don't include in payload
        full_name: fullName,
        first_name: form.first_name,
        last_name: form.last_name,
        middle_name: form.middle_name || null,
        date_of_birth: form.date_of_birth || null,
        gender: form.gender || null,
        nationality: form.nationality || null,
        national_id: form.national_id || null,
        passport_number: form.passport_number || null,
        phone: form.phone,
        email: form.email,
        alternate_email: form.alternate_email || null,
        address: form.address || null,
        city: form.city || null,
        state: form.state || null,
        country: form.country || null,
        postal_code: form.postal_code || null,
        emergency_contact_name: form.emergency_contact_name || null,
        emergency_contact_phone: form.emergency_contact_phone || null,
        emergency_contact_relationship: form.emergency_contact_relationship || null,
        status: form.status,
        employment_type: form.employment_type,
        location: form.location,
        department: form.department || null,
        manager: form.manager || null,
        hr_manager: form.hr_manager || null,
        hire_date: form.hire_date,
        probation_end_date: form.probation_end_date || null,
        position_id: form.position_id || null,
        notes: form.notes || null
      };

      if (editId) {
        await updateEmployee(editId, payload);
        setSnackbar({ open: true, message: 'Employee updated successfully', severity: 'success' });
      } else {
        await createEmployee(payload);
        setSnackbar({ open: true, message: 'Employee created successfully', severity: 'success' });
      }

      handleCloseModal();
      fetchData();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to save employee',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const rowsWithSerial = useMemo(() => {
    return employees.map((employee, index) => ({
      ...employee,
      sn: pagination.page * pagination.rowsPerPage + index + 1
    }));
  }, [employees, pagination.page, pagination.rowsPerPage]);

  const columns = useMemo(() => [
    {
      field: 'sn',
      headerName: t('employees:columns.sn'),
      width: 80,
      sortable: false,
      filterable: false
    },
    {
      field: 'employee_id',
      headerName: t('employees:columns.employeeId'),
      flex: 1,
      minWidth: 120,
      sortable: false
    },
    {
      field: 'full_name',
      headerName: t('employees:columns.fullName'),
      flex: 1,
      minWidth: 150,
      sortable: false
    },
    {
      field: 'email',
      headerName: t('employees:columns.email'),
      flex: 1,
      minWidth: 180,
      sortable: false
    },
    {
      field: 'phone',
      headerName: t('employees:columns.phone'),
      flex: 1,
      minWidth: 120,
      sortable: false
    },
    {
      field: 'location',
      headerName: t('employees:columns.location'),
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const location = params.value || 'nigeria';
        const labels = {
          'nigeria': t('employees:nigeria'),
          'china': t('employees:china'),
          'other': t('employees:other')
        };
        return labels[location] || location;
      }
    },
    {
      field: 'department',
      headerName: t('employees:columns.department'),
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => params.row.department_name || 'N/A'
    },
    {
      field: 'status',
      headerName: t('employees:columns.status'),
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const status = params.value || 'active';
        const colorMap = {
          'active': 'success',
          'inactive': 'default',
          'on_leave': 'warning',
          'suspended': 'error',
          'terminated': 'error'
        };
        const labelMap = {
          'active': t('employees:active'),
          'inactive': t('employees:inactive'),
          'on_leave': t('employees:onLeave'),
          'suspended': t('employees:suspended'),
          'terminated': t('employees:terminated')
        };
        return (
          <Chip
            label={labelMap[status] || status}
            color={colorMap[status] || 'default'}
            size="small"
          />
        );
      }
    },
    {
      field: 'hire_date',
      headerName: t('employees:columns.hireDate'),
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        if (!params.value) return 'N/A';
        try {
          return format(new Date(params.value), 'MMM dd, yyyy');
        } catch {
          return 'N/A';
        }
      }
    },
    {
      field: 'actions',
      headerName: t('employees:columns.actions'),
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title={t('common:buttons.view')}>
            <IconButton
              size="small"
              color="info"
              onClick={() => handleViewEmployee(params.row)}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common:buttons.edit')}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleOpenModal(params.row)}
            >
              <Edit />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ], [t]);

  if (loading && employees.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C', mb: 2 }}>
          {t('employees:title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
            disabled={loading}
          >
            {t('common:buttons.refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenModal()}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {t('employees:addEmployee')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label={t('employees:search')}
                fullWidth
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder={t('employees:searchPlaceholder')}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select
                label={t('employees:status')}
                fullWidth
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                SelectProps={{ native: true }}
                InputLabelProps={{
                  shrink: true,
                }}
              >
                <option value="">{t('employees:all')}</option>
                <option value="active">{t('employees:active')}</option>
                <option value="inactive">{t('employees:inactive')}</option>
                <option value="on_leave">{t('employees:onLeave')}</option>
                <option value="suspended">{t('employees:suspended')}</option>
                <option value="terminated">{t('employees:terminated')}</option>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select
                label={t('employees:location')}
                fullWidth
                value={filters.location}
                onChange={(e) => handleFilterChange('location', e.target.value)}
                SelectProps={{ native: true }}
                InputLabelProps={{
                  shrink: true,
                }}
              >
                <option value="">{t('employees:all')}</option>
                <option value="nigeria">{t('employees:nigeria')}</option>
                <option value="china">{t('employees:china')}</option>
                <option value="other">{t('employees:other')}</option>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label={t('employees:department')}
                fullWidth
                value={filters.department}
                onChange={(e) => handleFilterChange('department', e.target.value)}
                placeholder={t('employees:department') + ' ID'}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minHeight: 520
          }}>
            <DataGrid
              rows={rowsWithSerial}
              columns={columns}
              disableSelectionOnClick
              loading={loading}
              hideFooterPagination
              hideFooter
              getRowSpacing={() => ({ top: 2, bottom: 2 })}
              sx={{
                border: 'none',
                minWidth: 1200,
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3'
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-row': {
                  marginBottom: '4px',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <CustomPagination
          page={pagination.page}
          totalPages={totalPages}
          rowsPerPage={pagination.rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          customRowsActive={customRowsActive}
          customRowsInput={customRowsInput}
          onCustomRowsInputChange={handleCustomRowsInputChange}
          onCustomRowsInputBlur={handleCustomRowsInputBlur}
        />
      </Box>

      {/* Add/Edit Employee Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="lg" fullWidth>
        <DialogTitle>
          {editId ? t('employees:addEmployee') + ' - ' + t('common:buttons.edit') : t('employees:addEmployee')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              {/* User Selection (only for new employees) */}
              {!editId && (
                <Grid size={{ xs: 12 }}>
                  <Autocomplete
                    options={users}
                    getOptionLabel={(option) => {
                      const displayParts = [];
                      if (option.username) displayParts.push(`@${option.username}`);
                      if (option.email) displayParts.push(option.email);
                      if (option.fullname) displayParts.push(`(${option.fullname})`);
                      if (option.phone) displayParts.push(`Phone: ${option.phone}`);
                      return displayParts.join(' ');
                    }}
                    loading={usersLoading}
                    onInputChange={(event, newInputValue, reason) => {
                      // Don't search if the user is selecting an option
                      if (reason === 'reset' || reason === 'select-option') {
                        return;
                      }

                      // Clear previous timeout
                      if (searchTimeout) {
                        clearTimeout(searchTimeout);
                      }

                      if (newInputValue && newInputValue.length >= 2) {
                        setUsersLoading(true);
                        const timeoutId = setTimeout(() => {
                          getUsersList({ search: newInputValue, page_size: 20 })
                            .then(res => {
                              const newUsers = res.results || res || [];
                              // If we have a selected user, make sure they stay in the list
                              if (form.user_id) {
                                const selectedUser = users.find(u => (u.sid || u.id) === form.user_id);
                                if (selectedUser && !newUsers.find(u => (u.sid || u.id) === form.user_id)) {
                                  newUsers.unshift(selectedUser);
                                }
                              }
                              setUsers(newUsers);
                              setUsersLoading(false);
                            })
                            .catch(() => {
                              setUsers([]);
                              setUsersLoading(false);
                            });
                        }, 300); // 300ms debounce
                        setSearchTimeout(timeoutId);
                      } else if (!newInputValue) {
                        // Only clear users if no user is selected
                        if (!form.user_id) {
                          setUsers([]);
                        }
                        setUsersLoading(false);
                      }
                    }}
                    onChange={(event, newValue) => {
                      setSelectedUser(newValue);
                      handleFormChange('user_id', newValue?.sid || newValue?.id || '');
                      if (newValue) {
                        handleFormChange('email', newValue.email || '');
                        handleFormChange('phone', newValue.phone || '');
                        if (newValue.fullname) {
                          handleFormChange('full_name', newValue.fullname);
                          const nameParts = newValue.fullname.split(' ');
                          if (nameParts.length >= 2) {
                            handleFormChange('first_name', nameParts[0]);
                            handleFormChange('last_name', nameParts[nameParts.length - 1]);
                            if (nameParts.length > 2) {
                              handleFormChange('middle_name', nameParts.slice(1, -1).join(' '));
                            } else {
                              handleFormChange('middle_name', '');
                            }
                          } else {
                            handleFormChange('first_name', newValue.fullname);
                            handleFormChange('last_name', '');
                            handleFormChange('middle_name', '');
                          }
                        }
                      } else {
                        // Clear selected user when selection is cleared
                        setSelectedUser(null);
                      }
                    }}
                    value={selectedUser}
                    filterOptions={(x) => x} // Disable built-in filtering since we do server-side search
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('employees:searchUsers')}
                        required
                        placeholder={t('employees:searchUsersPlaceholder')}
                        helperText={t('employees:searchUsersHelperText')}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>
              )}

              {/* Employee ID Section - Auto-generated, display only when editing */}
              {editId && (
                <>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="h6" sx={{ mb: 1, color: '#DC143C' }}>
                      {t('employees:employeeInformation')}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label={t('employees:columns.employeeId')}
                      value={form.employee_id || ''}
                      disabled
                      InputLabelProps={{ shrink: true }}
                      helperText={t('employees:autoGenerated')}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label={t('employees:employeeNumber')}
                      value={form.employee_number || ''}
                      disabled
                      InputLabelProps={{ shrink: true }}
                      helperText={t('employees:autoGenerated')}
                    />
                  </Grid>
                </>
              )}

              {/* Personal Information */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" sx={{ mb: 1, mt: 2, color: '#DC143C' }}>
                  {t('employees:personalInformation')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label={t('employees:fullName')}
                  value={form.full_name}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleFormChange('full_name', value);
                    // Split the full name into first, middle, last for backend compatibility
                    const nameParts = value.trim().split(' ');
                    if (nameParts.length >= 2) {
                      handleFormChange('first_name', nameParts[0]);
                      handleFormChange('last_name', nameParts[nameParts.length - 1]);
                      if (nameParts.length > 2) {
                        handleFormChange('middle_name', nameParts.slice(1, -1).join(' '));
                      } else {
                        handleFormChange('middle_name', '');
                      }
                    } else {
                      handleFormChange('first_name', value);
                      handleFormChange('last_name', '');
                      handleFormChange('middle_name', '');
                    }
                  }}
                  required
                  placeholder={t('employees:fullNamePlaceholder')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('employees:dateOfBirth')}
                  type="date"
                  value={form.date_of_birth}
                  onChange={(e) => handleFormChange('date_of_birth', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ inputProps: { max: new Date().toISOString().split('T')[0] } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label={t('employees:gender')}
                  value={form.gender}
                  onChange={(e) => handleFormChange('gender', e.target.value)}
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="">{t('employees:selectGender')}</option>
                  <option value="male">{t('employees:male')}</option>
                  <option value="female">{t('employees:female')}</option>
                  <option value="other">{t('employees:other')}</option>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('employees:nationality')}
                  value={form.nationality}
                  onChange={(e) => handleFormChange('nationality', e.target.value)}
                  placeholder={t('employees:nationalityPlaceholder')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('employees:nationalId')}
                  value={form.national_id}
                  onChange={(e) => handleFormChange('national_id', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('employees:passportNumber')}
                  value={form.passport_number}
                  onChange={(e) => handleFormChange('passport_number', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Contact Information */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" sx={{ mb: 1, mt: 2, color: '#DC143C' }}>
                  {t('employees:contactInformation')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('employees:columns.phone')}
                  value={form.phone}
                  onChange={(e) => handleFormChange('phone', e.target.value)}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('employees:columns.email')}
                  type="email"
                  value={form.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('employees:alternateEmail')}
                  type="email"
                  value={form.alternate_email}
                  onChange={(e) => handleFormChange('alternate_email', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Address Information */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" sx={{ mb: 1, mt: 2, color: '#DC143C' }}>
                  {t('employees:addressInformation')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label={t('employees:address')}
                  value={form.address}
                  onChange={(e) => handleFormChange('address', e.target.value)}
                  multiline
                  rows={2}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label={t('employees:city')}
                  value={form.city}
                  onChange={(e) => handleFormChange('city', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label={t('employees:state/Province')}
                  value={form.state}
                  onChange={(e) => handleFormChange('state', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label={t('employees:country')}
                  value={form.country}
                  onChange={(e) => handleFormChange('country', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('employees:postalCode')}
                  value={form.postal_code}
                  onChange={(e) => handleFormChange('postal_code', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Emergency Contact */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" sx={{ mb: 1, mt: 2, color: '#DC143C' }}>
                  {t('employees:emergencyContact')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label={t('employees:contactName')}
                  value={form.emergency_contact_name}
                  onChange={(e) => handleFormChange('emergency_contact_name', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label={t('employees:contactPhone')}
                  value={form.emergency_contact_phone}
                  onChange={(e) => handleFormChange('emergency_contact_phone', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label={t('employees:relationship')}
                  value={form.emergency_contact_relationship}
                  onChange={(e) => handleFormChange('emergency_contact_relationship', e.target.value)}
                  placeholder={t('employees:relationshipPlaceholder')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Employment Information */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" sx={{ mb: 1, mt: 2, color: '#DC143C' }}>
                  {t('employees:employmentInformation')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label={t('employees:status')}
                  value={form.status}
                  onChange={(e) => handleFormChange('status', e.target.value)}
                  SelectProps={{ native: true }}
                  required
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="active">{t('employees:active')}</option>
                  <option value="inactive">{t('employees:inactive')}</option>
                  <option value="on_leave">{t('employees:onLeave')}</option>
                  <option value="suspended">{t('employees:suspended')}</option>
                  <option value="terminated">{t('employees:terminated')}</option>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label={t('employees:employmentType')}
                  value={form.employment_type}
                  onChange={(e) => handleFormChange('employment_type', e.target.value)}
                  SelectProps={{ native: true }}
                  required
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="full_time">{t('employees:fullTime')}</option>
                  <option value="part_time">{t('employees:partTime')}</option>
                  <option value="contract">{t('employees:contract')}</option>
                  <option value="intern">{t('employees:intern')}</option>
                  <option value="consultant">{t('employees:consultant')}</option>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label={t('employees:location')}
                  value={form.location}
                  onChange={(e) => handleFormChange('location', e.target.value)}
                  SelectProps={{ native: true }}
                  required
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="nigeria">{t('employees:nigeria')}</option>
                  <option value="china">{t('employees:china')}</option>
                  <option value="other">{t('employees:other')}</option>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  options={departments}
                  getOptionLabel={(option) => `${option.name || ''} (${option.code || ''})`}
                  value={departments.find(d => d.id === form.department) || null}
                  onChange={(event, newValue) => {
                    handleFormChange('department', newValue?.id || '');
                    // Filter positions by selected department
                    if (newValue) {
                      const deptPositions = positions.filter(p => p.department === newValue.id);
                      // If only one position, auto-select it
                      if (deptPositions.length === 1) {
                        handleFormChange('position_id', deptPositions[0].id);
                      }
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('employees:department')}
                      placeholder={t('employees:department')}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  options={positions.filter(p => !form.department || p.department === form.department)}
                  getOptionLabel={(option) => `${option.title || ''} (${option.code || ''})`}
                  value={positions.find(p => p.id === form.position_id) || null}
                  onChange={(event, newValue) => {
                    handleFormChange('position_id', newValue?.id || '');
                  }}
                  disabled={!form.department}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('employees:position')}
                      placeholder={t('employees:selectPosition')}
                      helperText={!form.department ? t('employees:selectDepartmentFirst') : ''}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  options={managers}
                  getOptionLabel={(option) => `${option.first_name || ''} ${option.last_name || ''} (${option.employee_id || ''})`}
                  value={managers.find(m => m.id === form.manager) || null}
                  onChange={(event, newValue) => {
                    handleFormChange('manager', newValue?.id || '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('employees:manager')}
                      placeholder={t('employees:manager')}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  options={hrManagers}
                  getOptionLabel={(option) => `${option.username || option.email || ''} ${option.fullname ? `(${option.fullname})` : ''}`}
                  value={hrManagers.find(h => (h.sid || h.id) === form.hr_manager) || null}
                  onChange={(event, newValue) => {
                    handleFormChange('hr_manager', newValue?.sid || newValue?.id || '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('employees:hrManager')}
                      placeholder={t('employees:hrManager')}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('employees:hireDate')}
                  type="date"
                  value={form.hire_date}
                  onChange={(e) => handleFormChange('hire_date', e.target.value)}
                  required
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ inputProps: { max: new Date().toISOString().split('T')[0] } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('employees:probationEndDate')}
                  type="date"
                  value={form.probation_end_date}
                  onChange={(e) => handleFormChange('probation_end_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ 
                    inputProps: { 
                      min: form.hire_date || undefined,
                      max: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0]
                    } 
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label={t('employees:notes')}
                  value={form.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  multiline
                  rows={3}
                  placeholder={t('employees:additionalNotes')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={saving}>
            {t('common:buttons.cancel')}
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={saving}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {saving ? <CircularProgress size={20} /> : (editId ? t('common:buttons.update') : t('common:buttons.create'))}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Employee Modal */}
      <Dialog open={viewModalOpen} onClose={handleCloseViewModal} maxWidth="lg" fullWidth>
        <DialogTitle>
          {t('employees:title')} - {viewingEmployee?.employee_id || viewingEmployee?.employee_number}
        </DialogTitle>
        <DialogContent>
          {viewingEmployee && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                {/* Employee Header Info */}
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Typography variant="h6" sx={{ color: '#DC143C' }}>
                      {viewingEmployee.full_name}
                    </Typography>
                    <Chip
                      label={viewingEmployee.status === 'active' ? t('employees:active') : t('employees:inactive')}
                      color={viewingEmployee.status === 'active' ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Grid>

                {/* Personal Information */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6" sx={{ mb: 1, color: '#DC143C' }}>
                   
                    {t('employees:personalInformation')}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t('employees:employeeId')}</Typography>
                  <Typography variant="body1">{viewingEmployee.employee_id || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t('employees:employeeNumber')}</Typography>
                  <Typography variant="body1">{viewingEmployee.employee_number || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t('employees:dateOfBirth')}</Typography>
                  <Typography variant="body1">
                    {viewingEmployee.date_of_birth ? format(new Date(viewingEmployee.date_of_birth), 'MMM dd, yyyy') : 'N/A'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t('employees:gender')}</Typography>
                  <Typography variant="body1">{viewingEmployee.gender ? viewingEmployee.gender.charAt(0).toUpperCase() + viewingEmployee.gender.slice(1) : 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t('employees:nationality')}</Typography>
                  <Typography variant="body1">{viewingEmployee.nationality || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t('employees:nationalId')}</Typography>
                  <Typography variant="body1">{viewingEmployee.national_id || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t('employees:passportNumber')}</Typography>
                  <Typography variant="body1">{viewingEmployee.passport_number || 'N/A'}</Typography>
                </Grid>

                {/* Contact Information */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6" sx={{ mb: 1, mt: 3, color: '#DC143C' }}>
                    {t('employees:contactInformation')}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t('employees:phone')}</Typography>
                  <Typography variant="body1">{viewingEmployee.phone || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t('employees:email')}</Typography>
                  <Typography variant="body1">{viewingEmployee.email || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">{t('employees:alternateEmail')}</Typography>
                  <Typography variant="body1">{viewingEmployee.alternate_email || 'N/A'}</Typography>
                </Grid>

                {/* Address Information */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6" sx={{ mb: 1, mt: 3, color: '#DC143C' }}>
                    {t('employees:addressInformation')}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="text.secondary">{t('employees:address')}</Typography>
                  <Typography variant="body1">{viewingEmployee.address || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="body2" color="text.secondary">{t('employees:city')}</Typography>
                  <Typography variant="body1">{viewingEmployee.city || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="body2" color="text.secondary">{t('employees:state/Province')}</Typography>
                  <Typography variant="body1">{viewingEmployee.state || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="body2" color="text.secondary">{t('employees:country')}</Typography>
                  <Typography variant="body1">{viewingEmployee.country || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t('employees:postalCode')}</Typography>
                  <Typography variant="body1">{viewingEmployee.postal_code || 'N/A'}</Typography>
                </Grid>

                {/* Emergency Contact */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6" sx={{ mb: 1, mt: 3, color: '#DC143C' }}>
                    {t('employees:emergencyContact')}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="body2" color="text.secondary">{t('employees:contactName')}</Typography>
                  <Typography variant="body1">{viewingEmployee.emergency_contact_name || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="body2" color="text.secondary">{t('employees:contactPhone')}</Typography>
                  <Typography variant="body1">{viewingEmployee.emergency_contact_phone || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="body2" color="text.secondary">{t('employees:relationship')}</Typography>
                  <Typography variant="body1">{viewingEmployee.emergency_contact_relationship || 'N/A'}</Typography>
                </Grid>

                {/* Employment Information */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6" sx={{ mb: 1, mt: 3, color: '#DC143C' }}>
                    {t('employees:employmentInformation')}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">{t('employees:status')}</Typography>
                  <Typography variant="body1">
                    {viewingEmployee.status ? viewingEmployee.status.charAt(0).toUpperCase() + viewingEmployee.status.slice(1).replace('_', ' ') : 'N/A'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t('employees:employmentType')}</Typography>
                  <Typography variant="body1">
                    {viewingEmployee.employment_type ? viewingEmployee.employment_type.charAt(0).toUpperCase() + viewingEmployee.employment_type.slice(1).replace('_', ' ') : 'N/A'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t('employees:location')}</Typography>
                  <Typography variant="body1">
                    {viewingEmployee.location ? viewingEmployee.location.charAt(0).toUpperCase() + viewingEmployee.location.slice(1) : 'N/A'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t('employees:department')}</Typography>
                  <Typography variant="body1">{viewingEmployee.department_name || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t('employees:manager')}</Typography>
                  <Typography variant="body1">{viewingEmployee.manager_name || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t('employees:hrManager')}</Typography>
                  <Typography variant="body1">{viewingEmployee.hr_manager_username || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t('employees:hireDate')}</Typography>
                  <Typography variant="body1">
                    {viewingEmployee.hire_date ? format(new Date(viewingEmployee.hire_date), 'MMM dd, yyyy') : 'N/A'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t('employees:probationEndDate')}</Typography>
                  <Typography variant="body1">
                    {viewingEmployee.probation_end_date ? format(new Date(viewingEmployee.probation_end_date), 'MMM dd, yyyy') : 'N/A'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="text.secondary">{t('employees:notes')}</Typography>
                  <Typography variant="body1">{viewingEmployee.notes || 'N/A'}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewModal}>
            {t('common:buttons.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Employees;

